/* eslint-env browser, node */
const {
  WIND_FORECAST_CAP,
  WIND_FORECAST_MAX_AGE_MS,
  forecastCandidateIds,
  solarNoonUnix,
  nearestHourIndex
} = require('../utils/windMatch')

const CACHE_STORAGE_KEY = 'pha.windForecastCache.v1'
const CACHE_ENTRY_LIMIT = 100
const OPEN_METEO_FORECAST = 'https://api.open-meteo.com/v1/forecast'

let activePassKey = ''
let lastAppliedPassKey = ''

function resetWindForecastClient () {
  activePassKey = ''
  lastAppliedPassKey = ''
}

function clearWindForecastCache (storage) {
  resetWindForecastClient()
  const target = storage || (typeof localStorage !== 'undefined' ? localStorage : null)
  if (!target || typeof target.removeItem !== 'function') return
  try {
    target.removeItem(CACHE_STORAGE_KEY)
  } catch (error) {
    // Logout still continues. Do not log the cache key.
  }
}

function roundedCoordinate (value) {
  const number = Number(value)
  return Number.isFinite(number) ? number.toFixed(4) : ''
}

function forecastWatchKey (sources, onScreenOnly) {
  const points = (sources || []).map((source) => {
    const id = source && source.id != null ? String(source.id) : ''
    return `${id}@${roundedCoordinate(source && source.lat)},${roundedCoordinate(source && source.long)}`
  }).join('|')
  return `${onScreenOnly === false ? 'off' : 'on'}:${points}`
}

function locationKey (lat, long) {
  return `${Number(lat).toFixed(4)},${Number(long).toFixed(4)}`
}

function buildForecastUrl (points) {
  const latitudes = points.map((point) => Number(point.lat).toFixed(4)).join(',')
  const longitudes = points.map((point) => Number(point.long).toFixed(4)).join(',')
  return `${OPEN_METEO_FORECAST}?latitude=${latitudes}&longitude=${longitudes}` +
    '&hourly=wind_speed_10m,wind_direction_10m&daily=sunrise,sunset&timezone=auto' +
    '&forecast_days=7&wind_speed_unit=mph&timeformat=unixtime'
}

function emptyCache () {
  return { entries: {} }
}

function readCache (storage) {
  try {
    const raw = storage.getItem(CACHE_STORAGE_KEY)
    if (!raw) return emptyCache()
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !parsed.entries || typeof parsed.entries !== 'object') {
      return emptyCache()
    }
    return { entries: parsed.entries }
  } catch (error) {
    return emptyCache()
  }
}

function writeCache (storage, cache) {
  const entries = cache.entries || {}
  const keys = Object.keys(entries)
  if (keys.length > CACHE_ENTRY_LIMIT) {
    keys.sort((left, right) => (entries[left].fetchedAt || 0) - (entries[right].fetchedAt || 0))
    const extra = keys.length - CACHE_ENTRY_LIMIT
    for (let index = 0; index < extra; index += 1) delete entries[keys[index]]
  }
  try {
    storage.setItem(CACHE_STORAGE_KEY, JSON.stringify({ entries }))
  } catch (error) {
    // Cache is optional. Do not log the storage key or the payload.
  }
}

function copySeries (value) {
  return Array.isArray(value) ? value.slice() : null
}

function normalizeElement (element, fetchedAt) {
  if (!element || typeof element !== 'object') return null
  if (typeof element.timezone !== 'string' || !element.timezone) return null
  const hourly = element.hourly || {}
  const daily = element.daily || {}
  const time = copySeries(hourly.time)
  const speed = copySeries(hourly.wind_speed_10m)
  const direction = copySeries(hourly.wind_direction_10m)
  const dailyTime = copySeries(daily.time)
  const sunrise = copySeries(daily.sunrise)
  const sunset = copySeries(daily.sunset)
  if (!time || !speed || !direction || !dailyTime || !sunrise || !sunset) return null
  return {
    fetchedAt,
    timezone: element.timezone,
    hourly: {
      time,
      wind_speed_10m: speed,
      wind_direction_10m: direction
    },
    daily: {
      time: dailyTime,
      sunrise,
      sunset
    }
  }
}

function isFresh (entry, now) {
  return !!(entry && typeof entry.fetchedAt === 'number' && (now - entry.fetchedAt) < WIND_FORECAST_MAX_AGE_MS)
}

function logStatus (status, requested, skipped, logger) {
  const write = typeof logger === 'function' ? logger : console.info
  write('[windForecast] status', status, 'requested', requested, 'skipped', skipped)
}

function resolveStorage (storage) {
  if (storage) return storage
  if (typeof localStorage !== 'undefined') return localStorage
  return {
    getItem () { return null },
    setItem () {}
  }
}

function finitePoint (row) {
  const lat = Number(row && row.lat)
  const long = Number(row && row.long)
  if (!Number.isFinite(lat) || !Number.isFinite(long)) return null
  return { lat, long }
}

function localCalendarDate (date) {
  const value = date || new Date()
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addCalendarDays (ymd, days) {
  const parts = String(ymd || '').split('-').map(Number)
  const date = new Date(parts[0], (parts[1] || 1) - 1, parts[2] || 1)
  date.setDate(date.getDate() + days)
  return localCalendarDate(date)
}

function calendarDateInZone (unixSeconds, timeZone) {
  if (!Number.isFinite(unixSeconds) || !timeZone) return null
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date(unixSeconds * 1000))
    const year = parts.find((part) => part.type === 'year')
    const month = parts.find((part) => part.type === 'month')
    const day = parts.find((part) => part.type === 'day')
    if (!year || !month || !day) return null
    return `${year.value}-${month.value}-${day.value}`
  } catch (error) {
    return null
  }
}

function formatClock (unixSeconds, timeZone) {
  if (!Number.isFinite(unixSeconds) || !timeZone) return ''
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone
    }).format(new Date(unixSeconds * 1000)).replace(/\u202f/g, ' ').replace(/\u00a0/g, ' ')
  } catch (error) {
    return ''
  }
}

function formatFetchedAt (epochMs) {
  const date = new Date(epochMs)
  if (Number.isNaN(date.getTime())) return ''
  const month = date.toLocaleString('en-US', { month: 'short' })
  let hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const suffix = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12 || 12
  return `${month} ${date.getDate()}, ${date.getFullYear()} ${hours}:${minutes} ${suffix}`
}

function formatWind (direction, speed) {
  if (typeof direction !== 'number' || !Number.isFinite(direction)) return ''
  const degrees = `${Math.round(direction)}°`
  if (typeof speed !== 'number' || !Number.isFinite(speed)) return degrees
  const rounded = Math.round(speed * 10) / 10
  return `${degrees} · ${rounded} mph`
}

function stampDate (stamp, timeZone) {
  if (typeof stamp === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(stamp)) return stamp
  if (typeof stamp === 'number') return calendarDateInZone(stamp, timeZone)
  return null
}

function periodSample (entry, ymd, period) {
  if (!entry || typeof entry.timezone !== 'string' || !entry.timezone) {
    return { coverage: 'unavailable', reason: 'missing' }
  }
  const daily = entry.daily || {}
  const times = Array.isArray(daily.time) ? daily.time : []
  let dayIndex = -1
  for (let index = 0; index < times.length; index += 1) {
    if (stampDate(times[index], entry.timezone) === ymd) {
      dayIndex = index
      break
    }
  }
  if (dayIndex < 0) return { coverage: 'unavailable', reason: 'day' }
  const sunrise = daily.sunrise && daily.sunrise[dayIndex]
  const sunset = daily.sunset && daily.sunset[dayIndex]
  let target = null
  if (period === 'sunrise') target = sunrise
  else if (period === 'sunset') target = sunset
  else target = solarNoonUnix(sunrise, sunset)
  if (typeof target !== 'number' || !Number.isFinite(target)) {
    return { coverage: 'unavailable', reason: 'missing' }
  }
  const hourly = entry.hourly || {}
  const hourIndex = nearestHourIndex(hourly.time || [], target, hourly.wind_direction_10m || [])
  if (hourIndex == null) return { coverage: 'unavailable', reason: 'missing' }
  const direction = hourly.wind_direction_10m[hourIndex]
  const speed = hourly.wind_speed_10m ? hourly.wind_speed_10m[hourIndex] : null
  return {
    coverage: 'ok',
    reason: null,
    direction,
    speed,
    targetUnix: target
  }
}

async function loadWindForecasts (rows, options) {
  const settings = options || {}
  const now = typeof settings.now === 'number' ? settings.now : Date.now()
  const storage = resolveStorage(settings.storage)
  const fetchImpl = settings.fetchImpl || fetch
  const online = settings.online != null
    ? settings.online !== false
    : (typeof navigator === 'undefined' ? true : navigator.onLine !== false)
  const force = settings.force === true
  const byId = {}
  const located = []

  for (const row of rows || []) {
    if (!row || row.id == null || row.id === '') continue
    const id = String(row.id)
    const point = finitePoint(row)
    if (!point) {
      byId[id] = { missingLocation: true, notForecasted: false, forecast: null, stale: false }
      continue
    }
    located.push({ id, title: row.title || '', lat: point.lat, long: point.long })
  }

  const capped = forecastCandidateIds(located, WIND_FORECAST_CAP)
  const locatedById = new Map(located.map((row) => [row.id, row]))
  for (const id of capped.notForecasted) {
    byId[id] = { missingLocation: false, notForecasted: true, forecast: null, stale: false }
  }
  const toForecast = capped.forecasted.map((id) => locatedById.get(id)).filter(Boolean)
  const passKey = capped.forecasted.join('\n')
  activePassKey = passKey

  const cache = readCache(storage)
  const allFresh = toForecast.length > 0 && toForecast.every((row) =>
    isFresh(cache.entries[locationKey(row.lat, row.long)], now)
  )
  const skipHttp = !force && passKey === lastAppliedPassKey && allFresh
  const skipped = (rows || []).length - toForecast.length
  let httpStatus = null

  if (!skipHttp && toForecast.length > 0 && typeof fetchImpl === 'function') {
    try {
      const response = await fetchImpl(buildForecastUrl(toForecast), {
        cache: 'no-store',
        credentials: 'omit'
      })
      httpStatus = response.status
      logStatus(httpStatus, toForecast.length, skipped, settings.logger)
      if (response.ok) {
        const payload = await response.json()
        const elements = Array.isArray(payload) ? payload : [payload]
        const fetchedAt = typeof settings.now === 'number' ? settings.now : Date.now()
        const latest = readCache(storage)
        elements.forEach((element, index) => {
          const row = toForecast[index]
          if (!row) return
          const normalized = normalizeElement(element, fetchedAt)
          if (!normalized) return
          latest.entries[locationKey(row.lat, row.long)] = normalized
        })
        writeCache(storage, latest)
      } else if (response.text) {
        await response.text()
      }
    } catch (error) {
      httpStatus = 0
      logStatus(0, toForecast.length, skipped, settings.logger)
    }
  }

  if (activePassKey !== passKey) {
    return {
      ignored: true,
      httpStatus,
      requested: skipHttp ? 0 : toForecast.length,
      skipped,
      offline: !online,
      byId: {}
    }
  }

  lastAppliedPassKey = passKey
  const latest = readCache(storage)
  for (const row of toForecast) {
    const entry = latest.entries[locationKey(row.lat, row.long)] || null
    byId[row.id] = {
      missingLocation: false,
      notForecasted: false,
      forecast: entry,
      stale: !!(entry && !isFresh(entry, now))
    }
  }

  return {
    ignored: false,
    httpStatus,
    requested: skipHttp ? 0 : toForecast.length,
    skipped,
    offline: !online,
    byId
  }
}

module.exports = {
  CACHE_STORAGE_KEY,
  CACHE_ENTRY_LIMIT,
  resetWindForecastClient,
  clearWindForecastCache,
  forecastWatchKey,
  buildForecastUrl,
  loadWindForecasts,
  localCalendarDate,
  addCalendarDays,
  calendarDateInZone,
  formatClock,
  formatFetchedAt,
  formatWind,
  periodSample
}
